<template>
  <div class="news-page">
    <header class="page-head">
      <div class="container">
        <h1 class="page-head__title">Notícias</h1>
        <p class="page-head__lead">
          Comunicados e informações do sindicato dos bancários de Uberlândia e região.
        </p>
      </div>
    </header>

    <div class="container news-page__body">
      <p v-if="loading" class="news-page__status" role="status">Carregando notícias…</p>

      <ul v-else class="news-page__grid" :class="{ 'is-first-page': page === 1 }">
        <li v-for="card in newsHome" :key="card.id">
          <article>
            <router-link :to="{ path: `/post/news/${card.id}` }" class="news-card">
              <span class="news-card__frame">
                <img :src="card.url" :alt="card.title" class="news-card__image" loading="lazy" />
              </span>
              <p class="news-card__meta">
                <span class="news-card__category">{{ card.category }}</span>
                <time>{{ card.date }}</time>
              </p>
              <h2 class="news-card__title">{{ card.title }}</h2>
            </router-link>
          </article>
        </li>
      </ul>

      <nav v-if="countPages > 1" class="news-page__pagination" aria-label="Páginas de notícias">
        <v-pagination
          v-model="page"
          :length="countPages"
          :total-visible="7"
          @input="handlePaginationPageChange"
        />
      </nav>
    </div>
  </div>
</template>

<script>
import newsService from "../../../../service/newsService";

export default {
  name: "NewsHome",
  metaInfo: {
    title: "SEEB | Notícias",
    meta: [
      { name: "description", content: "Notícias e comunicados do sindicato do bancarios de Uberlândia" }
    ]
  },
  methods: {
    getNews() {
      this.loading = true;
      newsService.paginate({}, this.page).then(res => {
        const templateResponse = res.data;
        this.countPages = templateResponse.countPages;

        this.newsHome = templateResponse.data.map(news => {
          return {
            id: news.id,
            title: news.title,
            category: news.category,
            date: new Date(news.created_at).toLocaleDateString("pt-BR"),
            url: news.path_img_card
          };
        });
      }).finally(() => {
        this.loading = false;
      });
    },
    handlePaginationPageChange() {
      this.getNews();
      window.scrollTo({ top: 0 });
    },
  },
  mounted() {
    this.getNews();
  },
  data() {
    return {
      newsHome: [],
      page: 1,
      countPages: 0,
      loading: true,
    };
  }
};
</script>

<style lang="scss" scoped>
.news-page {
  padding-bottom: var(--section);

  &__body {
    padding-top: clamp(40px, 5vw, 64px);
  }

  &__status {
    color: var(--c-muted);
  }

  &__grid {
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 56px 32px;
  }

  // Na primeira página, a notícia mais recente abre a lista em linha inteira:
  // foto à esquerda, título à direita. As demais seguem em três colunas.
  &__grid.is-first-page > li:first-child {
    grid-column: 1 / -1;
    padding-bottom: 56px;
    border-bottom: 1px solid var(--c-line);

    .news-card {
      display: grid;
      grid-template-columns: minmax(0, 7fr) minmax(0, 5fr);
      gap: 16px clamp(32px, 4vw, 56px);
      align-items: center;
    }

    .news-card__frame {
      grid-row: span 2;
    }

    .news-card__meta {
      align-self: end;
      margin: 0;
    }

    .news-card__title {
      align-self: start;
      font-size: clamp(26px, 2.8vw, 34px);
      line-height: 1.15;
      letter-spacing: -0.015em;
      text-wrap: balance;
    }
  }

  &__pagination {
    display: flex;
    justify-content: center;
    margin-top: 72px;
  }

  @media (max-width: 1023px) {
    &__grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    &__grid.is-first-page > li:first-child .news-card {
      grid-template-columns: 1fr;
    }

    &__grid.is-first-page > li:first-child .news-card__frame {
      grid-row: auto;
      margin-bottom: 8px;
    }

    &__grid.is-first-page > li:first-child .news-card__title {
      font-size: var(--fs-h3);
    }
  }

  @media (max-width: 599px) {
    &__grid {
      grid-template-columns: 1fr;
      gap: 40px;
    }

    &__grid.is-first-page > li:first-child {
      padding-bottom: 40px;
    }
  }
}

.news-card {
  display: block;
  color: inherit;
  text-decoration: none;

  &__frame {
    display: block;
    border-radius: var(--radius);
    overflow: hidden;
    background: #e6e9ee;
  }

  &__image {
    display: block;
    width: 100%;
    aspect-ratio: calc(3 / 2);
    object-fit: cover;
    transition: transform 400ms var(--ease);
  }

  &__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 14px;
    margin: 18px 0 8px;
    font-size: var(--fs-meta);
    color: var(--c-muted);

    time {
      font-variant-numeric: tabular-nums;
    }
  }

  &__category {
    font-weight: 600;
    color: var(--c-navy-deep);
  }

  &__title {
    margin: 0;
    font-family: var(--font-display);
    font-weight: 500;
    font-size: var(--fs-h4);
    line-height: 1.25;
    color: var(--c-navy);
    text-wrap: pretty;
    text-decoration: underline;
    text-decoration-color: transparent;
    text-decoration-thickness: 1px;
    text-underline-offset: 5px;
    transition: text-decoration-color 160ms var(--ease);
  }

  &:hover &__title {
    text-decoration-color: currentColor;
  }

  &:hover &__image {
    transform: scale(1.02);
  }
}

// Paginação do Vuetify com a mesma linguagem do site.
.news-page__pagination ::v-deep .v-pagination__item,
.news-page__pagination ::v-deep .v-pagination__navigation {
  min-width: 44px;
  height: 44px;
  border-radius: var(--radius-sm);
  box-shadow: none;
  border: 1px solid var(--c-line);
  font-family: var(--font-text);
  font-size: 15px;
  color: var(--c-ink);
}

.news-page__pagination ::v-deep .v-pagination__item--active {
  background: var(--c-navy) !important;
  border-color: var(--c-navy);
  color: #fff;
}
</style>
